'use client';

import { Dispatch, SetStateAction, useRef, useState } from 'react';
import Button from '../button';
import { IconCollapse, IconCollapseUp } from '../icon';
import { useLocaleLink, useTranslations, useLocale } from '@fshr/context/i18n-context';
import { Checkbox, ErrorAlert, Link } from '../components';
import CollapseIcon from '../icon/collapse';
import CollapseUpIcon from '../icon/collapse-up';
import HorizontalLine from '../horizontal-line';
import { useEffect } from 'react';
import { useTealium } from '@fshr/context/tealium-context';
import { DailyRate, RatesAndTotalInformation, TotalInfo, BookingInformation } from '../types';
import { formatCurrency, formatDateInLocale } from '@fshr/shared';
import BevSafeText from '../bev-safe-text';

type InfoSectionProps = {
	label?: string;
	data?: string;
	largeText?: boolean;
};

type BookingDataType = {
	checkInCheckOutDate: string;
	checkInDate?: string;
	checkOutDate?: string;
} & BookingInformation &
	Partial<RatesAndTotalInformation>;

type AverageDailyRateProps = {
	dailyRateAverage: TotalInfo;
	dailyRates: DailyRate[];
};

type AverageDailyRateItemProps = Pick<InfoSectionProps, 'label' | 'data'>;

type StayInformationProps = {
	booking: BookingDataType;
	onSubmit: () => void;
	errorMessage?: string;
	hotelCode: string;
	goldenId: string;
};

const InfoSection = (props: InfoSectionProps) => {
	const { data, label, largeText = false } = props;

	return (
		<div className="w-full">
			<HorizontalLine />
			<div className="flex flex-col gap-[10px] md:gap-[0px] md:flex-row justify-between pt-[30px]">
				<h2
					className={
						largeText ? 'text-subtitle1 text-text-heading' : 'text-subtitle2 text-text-heading'
					}
				>
					<BevSafeText>{label}</BevSafeText>
				</h2>

				<p
					className={
						largeText ? 'text-subtitle2 text-text-heading' : 'text-text-primary text-caption'
					}
				>
					{data}
				</p>
			</div>
		</div>
	);
};

const AverageDailyRateItem = (props: AverageDailyRateItemProps) => {
	const { data, label } = props;

	return (
		<div className="flex flex-col md:flex-row justify-between pl-[40px]">
			<h3 className="text-text-heading text-subtitle-2">
				<BevSafeText>{label}</BevSafeText>
			</h3>
			<p className="text-caption text-text-primary">{data}</p>
		</div>
	);
};

const AverageDailyRate = (props: AverageDailyRateProps) => {
	const [showDailyRates, setShowDailyRates] = useState(false);

	const { dailyRateAverage, dailyRates } = props;

	const translations = useTranslations();

	const handleShowDailyRates = () => {
		setShowDailyRates(!showDailyRates);
	};

	return (
		<div className="flex flex-col w-full spacing-stack-m">
			<div className="flex flex-col w-full spacing-stack-2xs">
				<InfoSection
					label={translations('Average Daily Rate')}
					data={formatCurrency(Number(dailyRateAverage.amount), dailyRateAverage.currency)}
				/>
				<Button
					icon={showDailyRates ? <IconCollapse /> : <IconCollapseUp />}
					iconPosition="end"
					intent="unstyled"
					onClick={handleShowDailyRates}
					className="flex flex-row text-button-primary spacing-inline-2xs cursor-pointer"
				>
					<p className="text-caption underline">{translations('Show Daily Rate')}</p>
				</Button>
			</div>
			{showDailyRates ? (
				<div className="flex flex-col w-full spacing-stack-m">
					{dailyRates.map(({ totalDailyRate, date }, index) => (
						<AverageDailyRateItem
							key={`daily-rate-item-${index}`}
							label={date}
							data={formatCurrency(Number(totalDailyRate.amount), totalDailyRate.currency)}
						/>
					))}
				</div>
			) : null}
		</div>
	);
};

const TermsAndConditions = ({
	isSubmitted,
	isChecked,
	onCheck,
	fsRoomOfferCancellationPolicy,
}: {
	isChecked: boolean | string | number | null;
	onCheck: Dispatch<SetStateAction<boolean | string | number | null>>;
	fsRoomOfferCancellationPolicy: string;
	isSubmitted: boolean;
}) => {
	const translations = useTranslations();
	const [isCollapsed, setOnCollapse] = useState<boolean>(true);
	return (
		<div className="flex-col w-full spacing-stack-s">
			<div className="flex spacing-inline-s">
				<div className="flex-col">
					<Checkbox
						aria-label={translations('Terms and Condition')}
						options={{ checked: true, unchecked: false }}
						onChange={value => onCheck(value)}
					/>
				</div>
				<div className="flex flex-col spacing-stack-2xs">
					<div className="flex gap-[5px]">
						<p className="text-body3">{translations('I have read & accepted the')}</p>{' '}
						<Button
							intent="unstyled"
							aria-expanded={isCollapsed}
							onClick={() => setOnCollapse(!isCollapsed)}
							className="flex gap-[5px] items-center text-body3 underline cursor-pointer"
						>
							{translations('Terms & Conditions')}
							{!isCollapsed ? <CollapseIcon /> : <CollapseUpIcon />}
						</Button>
					</div>
					{!isCollapsed ? (
						<div
							dangerouslySetInnerHTML={{ __html: translations(fsRoomOfferCancellationPolicy) }}
							className="text-caption"
						></div>
					) : null}
				</div>
			</div>
			{isSubmitted && !isChecked ? (
				<p className="text-caption text-[#EA0A0A] py-[4px] md:py-[6px]">
					{translations('To proceed, please accept terms and conditions.')}
				</p>
			) : null}
			{/* Legal text with proper spacing */}
			<div className="flex flex-col w-full spacing-stack-s mt-[15px]">
				<p className="text-body3 leading-relaxed">
					{translations(
						'All modifications are subject to availability at the time of modification and are only valid upon receipt of a confirmation number. Offers are not valid in conjunction with any other offer or contract and do not apply to groups. Rates displayed are per room, per night, and do not include taxes, service charges, administrative fees and gratuities unless otherwise noted.'
					)}
				</p>
			</div>
		</div>
	);
};

const FooterSection = ({
	onSubmit,
	fsRoomOfferCancellationPolicy,
	error,
	confirmationNumber,
	hotelCode,
}: {
	onSubmit?: () => void;
	fsRoomOfferCancellationPolicy: string;
	error?: string;
	hotelCode: string;
	confirmationNumber: string;
}) => {
	const translations = useTranslations();
	const [checkValue, setOnCheck] = useState<boolean | string | number | null>(false);
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
	const localeLink = useLocaleLink();
	const errorRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		errorRef.current?.focus();
	}, [error]);

	return (
		<>
			<TermsAndConditions
				isSubmitted={isSubmitted}
				isChecked={checkValue}
				fsRoomOfferCancellationPolicy={fsRoomOfferCancellationPolicy}
				onCheck={setOnCheck}
			/>
			{error ? (
				<div tabIndex={-1} ref={errorRef}>
					<ErrorAlert message={error ?? 'Unexpected error'} />{' '}
				</div>
			) : null}
			<HorizontalLine />
			<div className="flex flex-col-reverse md:flex-row justify-end gap-150 md:gap-100">
				<Link
					href={localeLink(`/profile/my-stays/${hotelCode}/${confirmationNumber}`)}
					type="button"
					intent="outline"
					className="w-full md:w-auto"
				>
					{translations('cancel')}
				</Link>
				<Button
					onClick={() => {
						setIsSubmitted(true);
						if (checkValue && onSubmit) return onSubmit();
					}}
					type="submit"
					intent="filled"
					className="w-full md:w-auto"
				>
					{translations('submit')}
				</Button>
			</div>
		</>
	);
};

/**
 * Component used to show a summary related about a booking data
 *
 * @param { BookingData } props.bookingData - Booking information
 * @returns { FC } - Component used to display a related to a given booking
 */
export default function EditStayInformationResumeComponent(props: StayInformationProps) {
	const { booking, onSubmit, errorMessage, hotelCode, goldenId } = props;
	const translations = useTranslations();
	const locale = useLocale();
	const { trackPage } = useTealium();
	useEffect(() => {
		trackPage({
			page_detail: 'edit_reservation:confirm_request',
			golden_id: goldenId,
		});
	}, []);

	return (
		<section
			className="w-full flex flex-col items-start gap-[30px]"
			aria-labelledby="section-title"
		>
			<h1 id="section-title" className="spacing-heading-m text-h3 text-text-heading">
				{translations('Change Your Stay Dates')}
			</h1>
			<div className="flex flex-col items-stretch spacing-stack-l gap-[30px]">
				<InfoSection
					label={translations('Check In - Check Out')}
					data={
						booking.checkInDate && booking.checkOutDate
							? `${formatDateInLocale(booking.checkInDate, 'MMM d', 'yyyy-MM-dd', locale)} - ${formatDateInLocale(booking.checkOutDate, 'MMM d', 'yyyy-MM-dd', locale)}`
							: booking.checkInCheckOutDate
					}
				/>
				<InfoSection label={translations('Room Type')} data={booking.room.type ?? ''} />
				<InfoSection label={translations('Offer')} data={booking.ratePlanTitle} />

				<AverageDailyRate
					dailyRates={booking.dailyRates ?? []}
					dailyRateAverage={
						booking.averageNigthlyPrice ?? {
							amount: 0,
							currency: 'USDC',
						}
					}
				/>

				<InfoSection
					label={translations('Total Room Rate')}
					data={formatCurrency(
						Number(booking.totalRoomRate?.amount) || 0,
						booking.totalRoomRate?.currency || 'USD'
					)}
				/>
				<InfoSection
					label={translations('Service Charge (5% of room rate)')}
					data={formatCurrency(
						Number(booking?.serviceCharge?.amount) || 0,
						booking?.serviceCharge?.currency || 'USD'
					)}
				/>
				<InfoSection
					label={translations('Estimated Room Total')}
					data={formatCurrency(
						Number(booking?.estimatedTotal?.amount) || 0,
						booking?.estimatedTotal?.currency || 'USD'
					)}
					largeText={true}
				/>
				<div className="spacing-stack-l"></div>
				<FooterSection
					fsRoomOfferCancellationPolicy={booking.cancelationPoliciy ?? ''}
					onSubmit={onSubmit}
					error={errorMessage}
					confirmationNumber={booking.confirmationNumber ?? ''}
					hotelCode={hotelCode}
				/>
			</div>
		</section>
	);
}
